import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Search,
  X,
  ChevronDown,
  TrendingUp,
  AlertCircle,
  search
} from 'lucide-react';
import { api } from '../services/api';

/**
 * Ce que dit l'étiquette de type, et de quelle couleur.
 *
 * `type_detail` vient du serveur. Cantine et transport y étaient absents :
 * tout ce qui n'était pas une inscription s'affichait « Frais scolaires »,
 * y compris un repas de cantine.
 */
const TYPES_ENCAISSEMENT = {
  inscription: { libelle: 'Frais fixes', couleur: 'bg-[#246990]' },
  scolaire: { libelle: 'Frais scolaires', couleur: 'bg-[#c66d59]' },
  examen: { libelle: 'Frais d\'examens', couleur: 'bg-[#319190]' },
  annexe_cantine: { libelle: 'Frais cantine', couleur: 'bg-[#3084b5]' },
  annexe_transport: { libelle: 'Frais transport', couleur: 'bg-[#319190]' },
  annexe: { libelle: 'Frais annexes', couleur: 'bg-[#3084b5]' }
};

const TableauEncaissements = ({
  anneeActive,
  selectedDate,
  filterPeriode,
  bornesMois = null,
  formatDateForAPI,
  formatDateForDisplay,
  formatMontant
}) => {
  // ============================================================================
  // ÉTATS
  // ============================================================================
  const [encaissements, setEncaissements] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [familleSearchTerm, setFamilleSearchTerm] = useState('');
  const [isFamilleDropdownOpen, setIsFamilleDropdownOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 20;

  // Filtres
  const [filters, setFilters] = useState({
    search: '',
    famille_id: '',
    type_encaissement: 'all'
  });

  // Debounce pour la recherche
  const [searchDebounce, setSearchDebounce] = useState('');

  // Refs pour éviter les rechargements inutiles
  const lastLoadParams = useRef(null);
  const isLoadingRef = useRef(false);

  // Le chargement était volontairement muet : `isLoadingRef` est une ref, elle
  // ne provoque aucun rendu. Changer de filtre ne montrait donc rien, et le
  // filtre avait l'air de ne pas répondre. Ce drapeau-ci est un état, et ne
  // couvre QUE le rafraîchissement du tableau — le premier chargement garde
  // le sien.
  const [rafraichissement, setRafraichissement] = useState(false);

  // Les paramètres dont on attend la réponse.
  //
  // Les deux effets appellent avec `force`, ce qui court-circuite le garde-fou
  // « un chargement est déjà en cours » : deux requêtes peuvent se croiser, et
  // c'est alors la dernière ARRIVÉE qui gagne, non la dernière DEMANDÉE. Un
  // clic rapide d'un filtre à l'autre affichait les lignes du filtre précédent
  // — précisément le symptôme d'un filtre qui semble ne pas fonctionner.
  const parametresAttendus = useRef(null);

  // ============================================================================
  // FONCTION DE CHARGEMENT 100% SILENCIEUSE
  // ============================================================================
  const loadEncaissements = useCallback(async (force = false) => {
    if (!anneeActive) return;

    // Éviter les rechargements multiples
    if (isLoadingRef.current && !force) {
      console.log('⏸️ Chargement déjà en cours, ignoré');
      return;
    }

    const params = {
      page: currentPage,
      limit: itemsPerPage,
      annee_scolaire_id: anneeActive.id
    };

    // Dates
    // Les bornes, quel que soit le mode. Ne traiter que le jour laissait le
    // mode mois sans aucune date -- et la route /encaissements ne connait pas
    // la notion de periode : sans bornes, elle rend toute l'annee.
    if (filterPeriode === 'jour') {
      const jour = formatDateForAPI(selectedDate);
      params.date_debut = jour;
      params.date_fin = jour;
    } else if (bornesMois?.dateDebut && bornesMois?.dateFin) {
      params.date_debut = bornesMois.dateDebut;
      params.date_fin = bornesMois.dateFin;
    }

    // Filtres
    if (filters.search.length >= 2) params.search = filters.search;
    if (filters.famille_id) params.famille_id = filters.famille_id;
    if (filters.type_encaissement !== 'all') params.type_encaissement = filters.type_encaissement;

    // Vérifier si les paramètres ont changé
    const paramsString = JSON.stringify(params);
    if (lastLoadParams.current === paramsString && !force) {
      console.log('⏸️ Paramètres identiques, chargement ignoré');
      return;
    }

    lastLoadParams.current = paramsString;
    parametresAttendus.current = paramsString;
    isLoadingRef.current = true;
    setRafraichissement(true);

    try {
      console.log('🔄 Chargement silencieux en arrière-plan:', params);

      const response = await api.portefeuille.getEncaissements(params);

      // Périmée : l'écran a changé de filtre entre-temps.
      if (parametresAttendus.current !== paramsString) return;

      if (response.success) {
        setEncaissements(response.data);
        setFamilles(response.familles_disponibles || []);
        setTotal(response.pagination.total);
        setTotalPages(response.pagination.total_pages);
        console.log('✅ Chargement terminé:', response.data.length);
        setIsInitialLoad(false);
      }
    } catch (error) {
      console.error('❌ Erreur chargement:', error);
    } finally {
      isLoadingRef.current = false;
      // Une requête périmée ne baisse pas le loader : une plus récente tourne
      // encore, et le tableau n'est pas à jour pour autant.
      if (parametresAttendus.current === paramsString) setRafraichissement(false);
    }
  }, [anneeActive, currentPage, filters, filterPeriode, selectedDate, bornesMois, formatDateForAPI]);

  // ============================================================================
  // EFFETS OPTIMISÉS
  // ============================================================================

  // Charger uniquement au montage ou changement de date/période/année
  useEffect(() => {
    if (anneeActive) {
      loadEncaissements(true);
    }
  }, [anneeActive?.id, filterPeriode, selectedDate, bornesMois]);

  // Charger quand les filtres changent
  useEffect(() => {
    if (!isInitialLoad && anneeActive) {
      const timer = setTimeout(() => {
        loadEncaissements(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [filters.famille_id, filters.type_encaissement, filters.search, currentPage]);

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchDebounce !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchDebounce }));
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchDebounce]);

  // Réinitialiser la page quand on change de filtre
  useEffect(() => {
    if (!isInitialLoad) {
      setCurrentPage(1);
    }
  }, [filters.famille_id, filters.type_encaissement]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleResetFilters = () => {
    setFilters({
      search: '',
      famille_id: '',
      type_encaissement: 'all'
    });
    setSearchDebounce('');
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.search || filters.famille_id || filters.type_encaissement !== 'all';

  // ============================================================================
  // RENDU - 100% SANS INDICATEURS DE CHARGEMENT
  // ============================================================================
  return (
    <Card>
      <CardContent className="pt-6">

        {/* FILTRES */}
        <div className="mb-6 space-y-4">

          {/* Ligne 1 : Recherche + Famille */}
          <div className="flex items-center gap-3">
            {/* Barre de recherche */}
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Rechercher par nom de famille ou nom d'élève..."
                value={searchDebounce}
                onChange={(e) => setSearchDebounce(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchDebounce && (
                <button
                  onClick={() => setSearchDebounce('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Filtre par famille */}
            {/* ✅ FAMILLE - Dropdown avec recherche */}
            <div className="relative min-w-[250px]">

              <div className="relative">
                {/* ⬅️ Icône Search */}
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 pointer-events-none z-10" />

                <input
                  type="text"
                  placeholder="Rechercher une famille..."
                  value={(() => {
                    if (filters.famille_id) {
                      const famille = familles.find(f => f.id.toString() === filters.famille_id);
                      return famille ? `${famille.nom} (${famille.nb_encaissements})` : '';
                    }
                    return familleSearchTerm || '';
                  })()}
                  onChange={(e) => {
                    setFamilleSearchTerm(e.target.value);
                    setIsFamilleDropdownOpen(true);
                    if (filters.famille_id) {
                      setFilters(prev => ({ ...prev, famille_id: '' }));
                    }
                  }}
                  onFocus={() => setIsFamilleDropdownOpen(true)}
                  className="w-full h-10 pl-10 pr-10 text-sm border border-gray-300 rounded-lg
                bg-white hover:border-[#319190] focus:border-[#319190] focus:ring-4 focus:ring-[#319190]/10 
                outline-none transition-all"
                />

                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </div>

                {/* ✅ Dropdown vers le BAS */}
                {isFamilleDropdownOpen && !filters.famille_id && (
                  <>
                    {/* Overlay pour fermer au clic extérieur */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => {
                        setIsFamilleDropdownOpen(false);
                        setFamilleSearchTerm('');
                      }}
                    />

                    <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                      {/* Option "Toutes les familles" */}
                      <div
                        onClick={() => {
                          setFilters(prev => ({ ...prev, famille_id: '' }));
                          setFamilleSearchTerm('');
                          setIsFamilleDropdownOpen(false);
                        }}
                        className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b transition-colors bg-gray-50"
                      >
                        <div className="font-semibold text-gray-900 text-sm flex items-center justify-between">
                          <span>Toutes les familles</span>
                          <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                            {familles.length}
                          </span>
                        </div>
                      </div>

                      {/* Liste des familles filtrées */}
                      {(() => {
                        const filteredFamilles = familles.filter(famille =>
                          famille.nom.toLowerCase().includes((familleSearchTerm || '').toLowerCase())
                        ).sort((a, b) => a.nom.localeCompare(b.nom));

                        if (filteredFamilles.length === 0) {
                          return (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              Aucune famille trouvée
                            </div>
                          );
                        }

                        return filteredFamilles.map((famille) => (
                          <div
                            key={famille.id}
                            onClick={() => {
                              setFilters(prev => ({ ...prev, famille_id: famille.id.toString() }));
                              setFamilleSearchTerm('');
                              setIsFamilleDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b last:border-b-0 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 text-sm">
                                {famille.nom}
                              </span>
                              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold">
                                {famille.nb_encaissements}
                              </span>
                            </div>
                            {famille.total && (
                              <div className="text-xs text-gray-500 mt-1">
                                Total: {formatMontant(famille.total)}
                              </div>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Bouton réinitialiser */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                className="flex items-center gap-2"
              >
                <X size={16} />
                Réinitialiser
              </Button>
            )}
          </div>

          {/* Ligne 2 : Type d'encaissement */}
          <div className="flex items-center gap-3">
            <Button
              variant={filters.type_encaissement === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, type_encaissement: 'all' }))}
              className={
                filters.type_encaissement === 'all'
                  ? 'bg-[#319190] text-white rounded hover:bg-[#319190]'
                  : 'text-[#319190] border border-[#319190] rounded hover:bg-[#319190]/10'
              }
            >
              Tous les encaissements
            </Button>

            <Button
              variant={filters.type_encaissement === 'inscription' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, type_encaissement: 'inscription' }))}
              className={
                filters.type_encaissement === 'inscription'
                  ? 'bg-[#246990] text-white rounded hover:bg-[#246990]/90'
                  : 'rounded text-[#246990] border border-[#246990] hover:bg-[#246990]/10'
              }
            >
              Frais fixes (Inscription/Réinscription)
            </Button>

            <Button
              variant={filters.type_encaissement === 'scolaire' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, type_encaissement: 'scolaire' }))}
              className={
                filters.type_encaissement === 'scolaire'
                  ? 'bg-[#c66d59] text-white hover:bg-[#ae7467]/90'
                  : 'text-[#c66d59] border border-[#c66d59] hover:bg-[#c66d59]/10'
              }
            >
              Frais scolaires
            </Button>

            {/* Cantine et transport tombaient dans « Frais scolaires » faute
                de filtre propre — la carte « Total encaissé » les distinguait
                pourtant déjà. */}
            <Button
              variant={filters.type_encaissement === 'annexe' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilters(prev => ({ ...prev, type_encaissement: 'annexe' }))}
              className={
                filters.type_encaissement === 'annexe'
                  ? 'bg-[#3084b5] text-white rounded hover:bg-[#3084b5]/90'
                  : 'rounded text-[#3084b5] border border-[#3084b5] hover:bg-[#3084b5]/10'
              }
            >
              Frais annexes (Cantine/Transport)
            </Button>
          </div>

        </div>

        {/* Le rafraîchissement remplace le tableau par le même bloc de
            chargement que le premier affichage — c'est le motif de
            `TableauFraisExamens`. Sans lui, changer de filtre ne montrait
            rien et le filtre paraissait sans effet. */}
        {(isInitialLoad || rafraichissement) ? (
          <div className="flex justify-center items-center h-40">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-green-600 font-medium">Chargement...</span>
            </div>
          </div>
        ) : encaissements.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            {filterPeriode === 'jour' && selectedDate > new Date() ? (
              <>
                <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 font-medium">
                  Aucune donnée disponible pour cette date future
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Sélectionnez une date passée ou actuelle
                </p>
              </>
            ) : (
              <>
                <TrendingUp size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 font-medium">Aucun encaissement trouvé</p>
                <p className="text-gray-500 text-sm mt-2">
                  {hasActiveFilters
                    ? 'Essayez de modifier vos filtres'
                    : 'Les encaissements apparaîtront ici'
                  }
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* ✅ TABLEAU SANS AUCUN INDICATEUR DE CHARGEMENT */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-green-50 border-b-2 border-green-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-green-800 uppercase">Date & Heure</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-green-800 uppercase">Élève</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-green-800 uppercase">Classe</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-green-800 uppercase">Section</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-green-800 uppercase">Libellé</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-green-800 uppercase">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-green-800 uppercase">Montant</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-green-800 uppercase">Mode paiement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {encaissements.map((enc) => (
                    <tr
                      key={enc.id}
                      className={`hover:bg-green-50/50 transition-colors ${enc.est_cloturee ? 'bg-gray-50 opacity-75' : ''
                        }`}
                    >
                      {/* Date & Heure */}
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-gray-900 font-medium">
                            {new Date(enc.date_transaction).toLocaleDateString('fr-FR')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(enc.date_transaction).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </td>

                      {/* Élève */}
                      <td className="px-4 py-3">
                        {enc.eleve_nom ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {enc.eleve_prenom} {enc.eleve_nom}
                            </p>
                            {enc.famille_nom && (
                              <p className="text-xs text-blue-600 mt-1">
                                Famille {enc.famille_nom}
                              </p>
                            )}
                            {enc.eleve_matricule && (
                              <p className="text-xs text-gray-500">
                                {enc.eleve_matricule}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>

                      {/* Classe */}
                      <td className="px-4 py-3">
                        {enc.classe_nom ? (
                          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                            {enc.classe_nom}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>

                      {/* Section */}
                      <td className="px-4 py-3">
                        {enc.section_nom ? (
                          <span className="text-sm text-gray-700">
                            {enc.section_nom}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>

                      {/* Libellé.
                          Les frais annexes reçoivent du serveur un libellé
                          lisible : « Frais annexes ponctuels - Cantine -
                          journalier - 2026-06-03 » se lisait comme un
                          identifiant, et cachait qu'un service de juin peut
                          être encaissé en août. */}
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900">
                          {enc.libelle_principal || enc.description}
                        </p>
                        {enc.libelle_complement && (
                          <p className="mt-0.5 text-[11px] text-amber-700">
                            {enc.libelle_complement}
                          </p>
                        )}
                      </td>

                      {/* Type.
                          Cantine et transport s'affichaient « Frais scolaires » :
                          l'étiquette disait le contraire de ce qui avait été
                          encaissé. */}
                      <td className="px-4 py-3">
                        <span className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold text-white ${TYPES_ENCAISSEMENT[enc.type_detail]?.couleur || TYPES_ENCAISSEMENT.scolaire.couleur}`}>
                          {TYPES_ENCAISSEMENT[enc.type_detail]?.libelle || TYPES_ENCAISSEMENT.scolaire.libelle}
                        </span>
                      </td>

                      {/* Montant */}
                      <td className="px-4 py-3 text-right">
                        <p className="text-sm font-bold text-green-600 whitespace-nowrap">
                          +{formatMontant(enc.montant)}
                        </p>
                      </td>

                      {/* Mode paiement */}
                      <td className="px-4 py-3 text-center">
                        {enc.mode_paiement ? (
                          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {enc.mode_paiement}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t pt-4">
                <p className="text-sm text-gray-600">
                  Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, total)} sur {total} encaissements
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </Button>
                  <span className="px-4 py-2 text-sm font-medium text-gray-700">
                    Page {currentPage} sur {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TableauEncaissements;